/**
 * WhatsApp connect via the unified engine. Two-step phone-OTP: send a code to a
 * phone number, then verify it. Both steps go through `/me/connections` so the
 * sender credentials (phone-number id / access token / OTP template) resolve
 * from the per-workspace External APIs config (config → env chain) — not env
 * directly. The agent surface is identity-only (routes inbound WhatsApp msgs).
 */

import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { meConnectionsService } from "@/services/me-connections-service";

interface ConnectWhatsappDialogProps {
  onClose: () => void;
  onConnected: () => void;
}

export function ConnectWhatsappDialog({ onClose, onConnected }: ConnectWhatsappDialogProps) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await meConnectionsService.start("whatsapp", { phone: phone.trim() });
      const vid = (res.data?.verificationId as string | undefined) ?? undefined;
      if (!vid) throw new Error("Couldn't send the verification code.");
      setVerificationId(vid);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the code.");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await meConnectionsService.complete("whatsapp", { verificationId, code: code.trim() });
      if (!res.success) throw new Error("Verification failed.");
      toast.success("WhatsApp connected");
      onConnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Connect WhatsApp</DialogTitle>
          <DialogDescription>
            Verify your phone number once. We send a one-time code over WhatsApp;
            messages from this number then route to your TeamMate user.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="wa-phone">Phone number (E.164)</Label>
            <Input
              id="wa-phone"
              placeholder="+97450123456"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy || !!verificationId}
              autoComplete="off"
            />
          </div>

          {verificationId ? (
            <div className="space-y-1.5">
              <Label htmlFor="wa-code">Verification code</Label>
              <Input
                id="wa-code"
                placeholder="123456"
                value={code}
                inputMode="numeric"
                maxLength={8}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                We sent a code to {phone} on WhatsApp. It expires shortly.
              </p>
            </div>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {verificationId ? (
            <Button onClick={verifyCode} disabled={busy || code.trim().length < 4}>
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Verify
            </Button>
          ) : (
            <Button onClick={sendCode} disabled={busy || phone.trim().length < 5}>
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Send code
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
